import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Session } from 'state';
import { nativeRequest } from './transport';

type Result = { session_id: number; eligible_asset_ids: number[] };
const EMPTY_IDS = new Set<number>();

/** Keep native picker support local and invalidate it when the saved definition changes. */
export default function useFastAssetEligibility(session: Session) {
  const isFast = session.execution_engine === 'fast';
  const [revision, setRevision] = useState(0);
  const key = JSON.stringify([
    session.id,
    session.execution_engine,
    session.reporting_currency,
    session.start_date,
    session.end_date,
    revision,
  ]);
  const [response, setResponse] = useState<{
    key: string;
    ids: number[] | null;
    error: string | null;
  } | null>(null);
  const reload = useCallback(() => setRevision(value => value + 1), []);
  useEffect(() => {
    if (!isFast) return;
    // Returning to a previous definition must await a fresh read, not reuse its old allowed IDs.
    setResponse({ key, ids: null, error: null });
    const controller = new AbortController();
    nativeRequest<Result>(
      `/sessions/${session.id}/fast-eligible-assets/`,
      undefined,
      controller.signal,
    )
      .then(result => {
        if (!controller.signal.aborted) {
          if (!result || result.session_id !== session.id)
            throw new Error('Invalid fast asset response.');
          setResponse({ key, ids: result.eligible_asset_ids, error: null });
        }
      })
      .catch(reason => {
        if (!controller.signal.aborted)
          setResponse({ key, ids: null, error: String(reason) });
      });
    return () => controller.abort();
  }, [isFast, session.id, key]);
  const current = response?.key === key ? response : null;
  const ids = current?.ids;
  const eligibleIds = useMemo(() => (ids ? new Set(ids) : EMPTY_IDS), [ids]);
  const allows = useCallback(
    (id: number) => !isFast || eligibleIds.has(id),
    [isFast, eligibleIds],
  );
  const ready = !isFast || ids != null;
  return {
    isFast,
    allows,
    ready,
    loading: isFast && !ready && !current?.error,
    error: isFast ? current?.error : null,
    reload,
  };
}
